import { TILE_SIZE, UNIT_HALF_HEIGHT } from './constants';
import { readGlbTriangles, type Triangles } from './glb';
import { v3, type V3 } from './vec';

/**
 * How a piece collides: its render mesh, the convex hull of it (stairs become ramps the
 * controller walks up), boxes, its mesh only while the starting gates are shut, or not at all.
 * Boxes cost Rapier a fraction of a mesh, so pieces units brush against all match long use them.
 */
export type PieceCollision = 'mesh' | 'hull' | 'box' | 'gate' | 'none';

/** Min and max corners in the model's own units. */
export type Box = [[number, number, number], [number, number, number]];

/** `scale` brings a kit built on another grid to one model unit per tile. */
export interface PieceDef {
  collision: PieceCollision;
  scale?: number;
  /** For `box`, traced from the mesh's cross-sections; the model's bounding box when absent. */
  boxes?: Box[];
  /** For `box`, regions that collide as the convex hull of the mesh's vertices inside: round parts. */
  hulls?: Box[];
}

/** Ids are GLB paths under `assets/models/`: Mini Arena at the root, other kits in their folder. */
export const PIECES = {
  floor: { collision: 'mesh' },
  'floor-detail': { collision: 'mesh' },
  block: { collision: 'mesh' },
  bricks: { collision: 'mesh' },
  'border-straight': { collision: 'mesh' },
  'border-corner': { collision: 'mesh' },
  wall: {
    collision: 'box',
    boxes: [
      [
        [-0.5, 0, -0.3],
        [0.5, 0.1, 0.3],
      ],
      [
        [-0.5, 0, -0.265],
        [0.5, 1, 0.265],
      ],
      [
        [-0.5, 0.787, -0.3],
        [0.5, 1, 0.3],
      ],
    ],
  },
  'wall-corner': { collision: 'mesh' },
  'wall-gate': { collision: 'gate' },
  column: {
    collision: 'box',
    boxes: [
      [
        [-0.3, 0, -0.3],
        [0.3, 0.15, 0.3],
      ],
      [
        [-0.25, 0.15, -0.25],
        [0.25, 0.25, 0.25],
      ],
      [
        [-0.287, 0.709, -0.2],
        [0.287, 0.85, 0.2],
      ],
      [
        [-0.3, 0.85, -0.3],
        [0.3, 1, 0.3],
      ],
    ],
    hulls: [
      [
        [-0.205, 0.249, -0.205],
        [0.205, 0.69, 0.205],
      ],
    ],
  },
  'column-damaged': { collision: 'mesh' },
  stairs: { collision: 'hull' },
  'stairs-corner': { collision: 'hull' },
  'stairs-corner-inner': { collision: 'mesh' },
  statue: { collision: 'mesh' },
  tree: { collision: 'mesh' },
  trophy: { collision: 'mesh' },
  'weapon-rack': { collision: 'box' },
  banner: { collision: 'none' },
  'dungeon/floor': { collision: 'mesh' },
  'dungeon/floor-detail': { collision: 'mesh' },
  'dungeon/wall': {
    collision: 'box',
    boxes: [
      [
        [-0.4, 0, -0.4],
        [0.4, 0.7, 0.4],
      ],
      [
        [-0.5, 0.7, -0.5],
        [0.5, 1, 0.5],
      ],
      [
        [0.4, 0, 0.3],
        [0.5, 0.7, 0.5],
      ],
      [
        [0.4, 0, -0.5],
        [0.5, 0.7, -0.3],
      ],
      [
        [-0.5, 0, 0.3],
        [-0.4, 0.7, 0.5],
      ],
      [
        [-0.5, 0, -0.5],
        [-0.4, 0.7, -0.3],
      ],
      [
        [0.3, 0, 0.4],
        [0.4, 0.7, 0.5],
      ],
      [
        [-0.4, 0, 0.4],
        [-0.3, 0.7, 0.5],
      ],
      [
        [0.3, 0, -0.5],
        [0.4, 0.7, -0.4],
      ],
      [
        [-0.4, 0, -0.5],
        [-0.3, 0.7, -0.4],
      ],
      [
        [-0.5, 1, -0.5],
        [-0.3, 1.1, -0.3],
      ],
      [
        [-0.5, 1, 0.3],
        [-0.3, 1.1, 0.5],
      ],
      [
        [0.3, 1, -0.5],
        [0.5, 1.1, -0.3],
      ],
      [
        [0.3, 1, 0.3],
        [0.5, 1.1, 0.5],
      ],
    ],
  },
  'dungeon/wall-opening': { collision: 'mesh' },
  'dungeon/gate': { collision: 'gate' },
  'dungeon/column': { collision: 'mesh' },
  'dungeon/rocks': { collision: 'mesh' },
  'dungeon/barrel': { collision: 'mesh' },
  'dungeon/chest': { collision: 'mesh' },
  'dungeon/banner': { collision: 'none' },
  'graveyard/stone-wall': {
    collision: 'box',
    boxes: [
      [
        [-0.5, 0, -0.5],
        [0.5, 0.19, -0.3],
      ],
      [
        [-0.5, 0, -0.477],
        [0.5, 0.71, -0.323],
      ],
      [
        [-0.5, 0.55, -0.493],
        [0.5, 0.68, -0.307],
      ],
    ],
  },
  'graveyard/iron-fence-border-gate': {
    collision: 'box',
    boxes: [
      [
        [-0.5, 0, -0.437],
        [-0.28, 0.23, -0.213],
      ],
      [
        [-0.467, 0.23, -0.403],
        [-0.31, 0.65, -0.247],
      ],
      [
        [-0.5, 0.65, -0.437],
        [-0.28, 0.76, -0.213],
      ],
      [
        [0.28, 0, -0.437],
        [0.5, 0.23, -0.213],
      ],
      [
        [0.31, 0.23, -0.403],
        [0.467, 0.65, -0.247],
      ],
      [
        [0.28, 0.65, -0.437],
        [0.5, 0.76, -0.213],
      ],
    ],
  },
  'graveyard/iron-fence': { collision: 'gate' },
  'graveyard/crypt': { collision: 'mesh' },
  'graveyard/gravestone-cross': { collision: 'mesh' },
  'graveyard/gravestone-round': { collision: 'mesh' },
  'graveyard/gravestone-wide': { collision: 'mesh' },
  'graveyard/gravestone-broken': { collision: 'mesh' },
  'graveyard/pillar-obelisk': { collision: 'mesh' },
  'graveyard/lightpost-single': { collision: 'mesh' },
  'graveyard/pine': { collision: 'mesh' },
  'graveyard/pumpkin-carved': { collision: 'mesh' },
  'graveyard/grave': { collision: 'mesh' },
  'graveyard/candle-multiple': { collision: 'none' },
  'graveyard/lantern-candle': { collision: 'none' },
  'graveyard/fire-basket': { collision: 'mesh' },
  'forest/target': { collision: 'mesh' },
  'dungeon/stairs': { collision: 'hull' },
  'dungeon/wall-half': { collision: 'mesh' },
  'castle/wall': {
    collision: 'box',
    boxes: [
      [
        [-0.48, 0, -0.5],
        [0.48, 1.18, 0.5],
      ],
      [
        [-0.5, 1.18, -0.5],
        [-0.31, 1.26, 0.5],
      ],
      [
        [0.31, 1.18, -0.5],
        [0.5, 1.26, 0.5],
      ],
      [
        [-0.5, 1.26, -0.5],
        [-0.33, 1.31, -0.25],
      ],
      [
        [-0.5, 1.26, 0],
        [-0.33, 1.31, 0.25],
      ],
      [
        [0.33, 1.26, -0.5],
        [0.5, 1.31, -0.25],
      ],
      [
        [0.33, 1.26, 0],
        [0.5, 1.31, 0.25],
      ],
    ],
  },
  'castle/wall-half': {
    collision: 'box',
    boxes: [
      [
        [-0.48, 0, -0.5],
        [0.48, 1.18, 0.5],
      ],
      [
        [-0.5, 1.18, -0.5],
        [-0.31, 1.26, 0.5],
      ],
      [
        [-0.5, 1.26, -0.5],
        [-0.33, 1.31, -0.25],
      ],
      [
        [-0.5, 1.26, 0],
        [-0.33, 1.31, 0.25],
      ],
    ],
  },
  'castle/metal-gate': { collision: 'gate' },
  'castle/tower-square-base': { collision: 'mesh' },
  'castle/tower-square-mid': { collision: 'mesh' },
  'castle/tower-square-mid-windows': { collision: 'mesh' },
  'castle/tower-square-top-roof-high': { collision: 'mesh' },
  'castle/tower-square-arch': {
    collision: 'box',
    // Each leg widens in steps up the curve of the arches.
    boxes: [
      [
        [-0.467, 0, -0.467],
        [-0.245, 0.64, -0.245],
      ],
      [
        [-0.467, 0, 0.245],
        [-0.245, 0.64, 0.467],
      ],
      [
        [0.245, 0, -0.467],
        [0.467, 0.64, -0.245],
      ],
      [
        [0.245, 0, 0.245],
        [0.467, 0.64, 0.467],
      ],
      [
        [-0.467, 0.64, -0.467],
        [-0.17, 0.72, -0.17],
      ],
      [
        [-0.467, 0.64, 0.17],
        [-0.17, 0.72, 0.467],
      ],
      [
        [0.17, 0.64, -0.467],
        [0.467, 0.72, -0.17],
      ],
      [
        [0.17, 0.64, 0.17],
        [0.467, 0.72, 0.467],
      ],
      [
        [-0.467, 0.72, -0.467],
        [-0.1, 0.78, -0.1],
      ],
      [
        [-0.467, 0.72, 0.1],
        [-0.1, 0.78, 0.467],
      ],
      [
        [0.1, 0.72, -0.467],
        [0.467, 0.78, -0.1],
      ],
      [
        [0.1, 0.72, 0.1],
        [0.467, 0.78, 0.467],
      ],
      [
        [-0.467, 0.78, -0.467],
        [0.467, 1.01, 0.467],
      ],
    ],
  },
  'castle/tower-hexagon-base': { collision: 'mesh' },
  'castle/tower-hexagon-mid': { collision: 'mesh' },
  'castle/tower-hexagon-roof': { collision: 'mesh' },
  'castle/bridge-straight': { collision: 'mesh' },
  'castle/bridge-straight-pillar': {
    collision: 'box',
    boxes: [
      [
        [-0.18, 0, -0.39],
        [0.18, 0.49, 0.39],
      ],
      [
        [-0.28, 0.49, -0.39],
        [0.28, 0.7, 0.39],
      ],
      [
        [-0.467, 0.7, -0.43],
        [0.467, 0.9, 0.43],
      ],
      [
        [-0.467, 0.9, -0.467],
        [0.467, 1.04, 0.467],
      ],
      [
        [-0.467, 1.04, -0.467],
        [0.467, 1.12, -0.35],
      ],
      [
        [-0.467, 1.04, 0.35],
        [0.467, 1.12, 0.467],
      ],
      [
        [-0.467, 1.12, -0.467],
        [-0.35, 1.176, -0.35],
      ],
      [
        [-0.12, 1.12, -0.467],
        [0.12, 1.176, -0.35],
      ],
      [
        [0.35, 1.12, -0.467],
        [0.467, 1.176, -0.35],
      ],
      [
        [-0.467, 1.12, 0.35],
        [-0.35, 1.176, 0.467],
      ],
      [
        [-0.12, 1.12, 0.35],
        [0.12, 1.176, 0.467],
      ],
      [
        [0.35, 1.12, 0.35],
        [0.467, 1.176, 0.467],
      ],
    ],
  },
  'castle/rocks-large': { collision: 'mesh' },
  'castle/rocks-small': { collision: 'mesh' },
  'castle/tree-large': { collision: 'mesh' },
  'castle/tree-small': { collision: 'mesh' },
  'castle/siege-catapult': { collision: 'mesh' },
  'castle/siege-ballista-demolished': { collision: 'mesh' },
  'castle/flag-banner-long': { collision: 'none' },
  'pirate/rocks-a': { collision: 'mesh', scale: 0.4 },
  'pirate/rocks-b': { collision: 'mesh', scale: 0.4 },
  'pirate/rocks-c': { collision: 'mesh', scale: 0.4 },
  'pirate/rocks-sand-a': { collision: 'mesh', scale: 0.4 },
  'pirate/rocks-sand-b': { collision: 'mesh', scale: 0.4 },
  'pirate/rocks-sand-c': { collision: 'mesh', scale: 0.4 },
  'pirate/palm-straight': { collision: 'mesh', scale: 0.4 },
  'pirate/palm-bend': { collision: 'mesh', scale: 0.4 },
  'pirate/ship-wreck': { collision: 'mesh', scale: 0.4 },
  'pirate/ship-pirate-large': { collision: 'mesh', scale: 0.4 },
  'pirate/ship-ghost': { collision: 'mesh', scale: 0.4 },
  'pirate/ship-large': { collision: 'mesh', scale: 0.4 },
  'pirate/structure': {
    collision: 'box',
    scale: 0.4,
    boxes: [
      [
        [-1.14, 0, -1.14],
        [-0.86, 2, -0.86],
      ],
      [
        [-1.14, 0, 0.86],
        [-0.86, 2, 1.14],
      ],
      [
        [0.86, 0, -1.14],
        [1.14, 2, -0.86],
      ],
      [
        [0.86, 0, 0.86],
        [1.14, 2, 1.14],
      ],
      [
        [-1.25, 2, -1.25],
        [1.25, 2.2, 1.25],
      ],
    ],
  },
  'pirate/structure-platform-dock': { collision: 'mesh', scale: 0.4 },
  'pirate/barrel': { collision: 'mesh', scale: 0.4 },
  'pirate/crate': { collision: 'box', scale: 0.4 },
  'pirate/crate-bottles': { collision: 'mesh', scale: 0.4 },
  'pirate/cannon': { collision: 'mesh', scale: 0.4 },
  'pirate/boat-row-small': { collision: 'mesh', scale: 0.4 },
  'pirate/patch-grass': { collision: 'none', scale: 0.4 },
} satisfies Record<string, PieceDef>;

export type PieceId = keyof typeof PIECES;
export const PIECE_IDS = Object.keys(PIECES) as PieceId[];
export const isPieceId = (id: string): id is PieceId => id in PIECES;

/**
 * A piece on the tile grid. `level` counts half tiles up, the height of a block or stair. All
 * three are whole for structure; fractions place props freely, on terrain or decks.
 */
export interface PlacedPiece {
  piece: PieceId;
  x: number;
  z: number;
  level: number;
  /** Quarter turns about +y. */
  rot: number;
  /** Multiplies the piece's size; 1 when absent. */
  scale?: number;
}

export interface Spawn {
  team: 0 | 1;
  x: number;
  z: number;
  level: number;
}

/** Look of the world around the map. Colors are CSS hex strings; `fog` is [near, far] in world units. */
export interface MapEnv {
  sky?: string;
  /** The plane far below the map. */
  ground?: string;
  fog?: [number, number];
  /** Scales every light. */
  light?: number;
  sun?: string;
  terrain?: string;
  particles?: ParticleKind[];
  ambience?: AmbienceKind;
}

/** Ambient particles drifting around the camera. */
export const PARTICLE_KINDS = ['dust', 'fireflies', 'embers'] as const;
export type ParticleKind = (typeof PARTICLE_KINDS)[number];

/** The background sound loop; `forest` turns to its night loop after dark. */
export const AMBIENCE_KINDS = ['forest', 'beach', 'cave'] as const;
export type AmbienceKind = (typeof AMBIENCE_KINDS)[number];

/**
 * A terrain corner, `[x, z, level]`: corner (x, z) is the -x -z corner of cell (x, z). A cell is
 * ground when all four of its corners are set, so a map can be terrain in places and tiles elsewhere.
 */
export type TerrainCorner = [number, number, number];

export interface ArenaMap {
  name: string;
  pieces: PlacedPiece[];
  spawns: Spawn[];
  /** Where practice stands its training dummies; team 1's spawns when there are none. */
  dummies?: Omit<Spawn, 'team'>[];
  env?: MapEnv;
  terrain?: TerrainCorner[];
}

export const LEVEL_HEIGHT = TILE_SIZE / 2;

export const pieceOrigin = (p: { x: number; z: number; level: number }): V3 =>
  v3(p.x * TILE_SIZE, p.level * LEVEL_HEIGHT, p.z * TILE_SIZE);

/** World units per model unit. */
export const pieceScale = (placed: Pick<PlacedPiece, 'piece' | 'scale'>) => {
  const def: PieceDef = PIECES[placed.piece];
  return TILE_SIZE * (def.scale ?? 1) * (placed.scale ?? 1);
};

/**
 * Two up-facing triangles per terrain cell, in world space. Edges with no terrain cell beyond
 * get a skirt down (or up) to level 0, so a raised edge never floats with a gap a unit fits under.
 */
export function terrainTriangles(corners: TerrainCorner[]): Triangles {
  const heights = new Map(
    corners.map(([x, z, level]) => [`${x},${z}`, level * LEVEL_HEIGHT]),
  );
  const isCell = (x: number, z: number) =>
    [`${x},${z}`, `${x + 1},${z}`, `${x},${z + 1}`, `${x + 1},${z + 1}`].every(
      (key) => heights.has(key),
    );
  const positions: number[] = [];
  const indices: number[] = [];
  const vertex = (x: number, y: number, z: number) => {
    positions.push((x - 0.5) * TILE_SIZE, y, (z - 0.5) * TILE_SIZE);
    return positions.length / 3 - 1;
  };
  const height = (x: number, z: number) => heights.get(`${x},${z}`)!;

  for (const [x, z] of corners) {
    if (!isCell(x, z)) continue;
    const a = vertex(x, height(x, z), z);
    const b = vertex(x + 1, height(x + 1, z), z);
    const c = vertex(x + 1, height(x + 1, z + 1), z + 1);
    const d = vertex(x, height(x, z + 1), z + 1);
    indices.push(a, d, c, a, c, b);

    // Each edge as its two corners and the cell across it.
    const edges: [number, number, number, number, number, number][] = [
      [x, z, x + 1, z, x, z - 1],
      [x + 1, z + 1, x, z + 1, x, z + 1],
      [x, z + 1, x, z, x - 1, z],
      [x + 1, z, x + 1, z + 1, x + 1, z],
    ];
    for (const [ax, az, bx, bz, nx, nz] of edges) {
      if (isCell(nx, nz)) continue;
      const ha = height(ax, az);
      const hb = height(bx, bz);
      const top = [vertex(ax, ha, az), vertex(bx, hb, bz)];
      const base = [vertex(ax, 0, az), vertex(bx, 0, bz)];
      // Skip the triangle that collapses where a corner already sits at level 0.
      if (hb !== 0) indices.push(top[0], top[1], base[1]);
      if (ha !== 0) indices.push(top[0], base[1], base[0]);
    }
  }
  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
  };
}

/** Where a unit spawned at `spawn` starts, a little above the floor so it settles onto it. */
export const spawnPosition = (spawn: Omit<Spawn, 'team'>): V3 => {
  const origin = pieceOrigin(spawn);
  return v3(origin.x, origin.y + UNIT_HALF_HEIGHT + 0.1, origin.z);
};

/** Model-space triangles of every piece, keyed by piece id. */
export type PieceGeometry = Partial<Record<PieceId, Triangles>>;

/** Reads each piece's GLB through `read` (a fetch on the client, a file read on the server). */
export async function loadPieceGeometry(
  read: (file: string) => Promise<ArrayBuffer>,
  ids: readonly PieceId[] = PIECE_IDS,
): Promise<PieceGeometry> {
  const entries = await Promise.all(
    ids.map(
      async (id) => [id, readGlbTriangles(await read(`${id}.glb`))] as const,
    ),
  );
  return Object.fromEntries(entries);
}

export interface MapColliders {
  /** All `mesh` pieces merged into one triangle mesh. */
  mesh: Triangles;
  /** One point cloud per `hull` piece and per box. */
  hulls: Float32Array[];
}

/** `gatesShut` adds the `gate` pieces, which only block until the match goes live. */
export function buildMapColliders(
  map: ArenaMap,
  geometry: PieceGeometry,
  gatesShut = false,
): MapColliders {
  const positions: number[] = [];
  const indices: number[] = [];
  const hulls: Float32Array[] = [];

  const terrain = terrainTriangles(map.terrain ?? []);
  for (const value of terrain.positions) positions.push(value);
  for (const index of terrain.indices) indices.push(index);

  for (const placed of map.pieces) {
    const collision = PIECES[placed.piece]?.collision ?? 'none';
    const source = geometry[placed.piece];
    if (collision === 'none' || !source) continue;
    if (collision === 'gate' && !gatesShut) continue;
    if (collision === 'box') {
      const def: PieceDef = PIECES[placed.piece];
      for (const box of def.boxes ?? [bounds(source.positions)])
        hulls.push(transformPoints(corners(box), placed));
      for (const region of def.hulls ?? [])
        hulls.push(transformPoints(inside(source.positions, region), placed));
      continue;
    }
    const points = transformPoints(source.positions, placed);
    if (collision === 'hull') {
      hulls.push(points);
      continue;
    }
    const base = positions.length / 3;
    for (const value of points) positions.push(value);
    for (const index of source.indices) indices.push(base + index);
  }

  return {
    mesh: {
      positions: new Float32Array(positions),
      indices: new Uint32Array(indices),
    },
    hulls,
  };
}

function bounds(positions: Float32Array): Box {
  const min: Box[0] = [Infinity, Infinity, Infinity];
  const max: Box[1] = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < positions.length; i++) {
    min[i % 3] = Math.min(min[i % 3], positions[i]);
    max[i % 3] = Math.max(max[i % 3], positions[i]);
  }
  return [min, max];
}

function inside(positions: Float32Array, [min, max]: Box): Float32Array {
  const out: number[] = [];
  for (let i = 0; i < positions.length; i += 3) {
    const p = [positions[i], positions[i + 1], positions[i + 2]];
    if (p.every((v, k) => v >= min[k] && v <= max[k])) out.push(...p);
  }
  return new Float32Array(out);
}

function corners([min, max]: Box): Float32Array {
  const out = new Float32Array(24);
  for (let i = 0; i < 8; i++) {
    out[i * 3] = i & 1 ? max[0] : min[0];
    out[i * 3 + 1] = i & 2 ? max[1] : min[1];
    out[i * 3 + 2] = i & 4 ? max[2] : min[2];
  }
  return out;
}

/** Model space to world: scale by `pieceScale`, turn `rot` quarter turns, move to the cell. */
function transformPoints(
  source: Float32Array,
  placed: PlacedPiece,
): Float32Array {
  const origin = pieceOrigin(placed);
  const size = pieceScale(placed);
  const angle = (placed.rot * Math.PI) / 2;
  const s = Math.sin(angle);
  const c = Math.cos(angle);
  const out = new Float32Array(source.length);
  for (let i = 0; i < source.length; i += 3) {
    const x = source[i] * size;
    const y = source[i + 1] * size;
    const z = source[i + 2] * size;
    // Three.js rotation.y: x' = x cos + z sin, z' = -x sin + z cos.
    out[i] = x * c + z * s + origin.x;
    out[i + 1] = y + origin.y;
    out[i + 2] = -x * s + z * c + origin.z;
  }
  return out;
}
